import type { FieldPolicy, InMemoryCache, Reference } from '@apollo/client';

/**
 * Cache behaviour for the Conversation's email list (`emailsByOrganization`).
 *
 * The list is cursor-paged, and the surface both scrolls back through it
 * ("Load older messages") and refetches its head — after a send, and on every
 * status-stream reconnect. Without a merge policy Apollo replaces the cached
 * list with whichever page came back last, so a refetch of the newest 25
 * silently threw away every older page the operator had loaded.
 *
 * This is unrelated to the delivery-status stream: that writes onto the
 * normalized `EmailCommunication` *entities* (`useEmailStatusStream`), which
 * this file never touches — the list only holds references to them.
 */

interface ConversationEdge {
  __typename?: string;
  node: Reference;
}

interface ConversationConnection {
  edges: ConversationEdge[];
  pageInfo?: { hasNextPage?: boolean; endCursor?: string | null };
  totalCount?: number;
}

const FIELD = 'emailsByOrganization';

/** Keyed by record and filters only: paging arguments must not fork the list. */
export const emailsByOrganizationFieldPolicy: FieldPolicy<
  ConversationConnection,
  ConversationConnection
> = {
  keyArgs: ['organizationId', 'contactEmails', 'participantEmails'],
  merge(existing, incoming, { args, readField }) {
    if (!existing) return incoming;

    const idOf = (edge: ConversationEdge): unknown => readField('id', edge.node);
    const timeOf = (edge: ConversationEdge): number => {
      const value = readField<string>('createdAt', edge.node);
      return value ? Date.parse(value) : Number.NaN;
    };

    // An older page: append what is new.
    if (args?.after) {
      const seen = new Set(existing.edges.map(idOf));
      return {
        ...incoming,
        edges: [...existing.edges, ...incoming.edges.filter((edge) => !seen.has(idOf(edge)))],
      };
    }

    // A head page with nothing after it is the whole list.
    if (!incoming.pageInfo?.hasNextPage) return incoming;

    // A head page with more behind it: it is authoritative for everything as new
    // as its oldest row, so an existing row inside that span that it does not
    // contain has been deleted (a discarded draft) and must not linger. Rows
    // older than the span are the pages the operator loaded — keep them.
    const incomingIds = new Set(incoming.edges.map(idOf));
    const oldest = Math.min(...incoming.edges.map(timeOf));
    const tail = existing.edges.filter(
      (edge) => !incomingIds.has(idOf(edge)) && (Number.isNaN(oldest) || timeOf(edge) <= oldest),
    );

    return {
      ...incoming,
      edges: [...incoming.edges, ...tail],
      // The deepest loaded page's cursor still describes where the tail ends.
      pageInfo: tail.length > 0 ? (existing.pageInfo ?? incoming.pageInfo) : incoming.pageInfo,
    };
  },
};

/** A modifier's `existing` is a store object or a `Reference`; only a stored connection has edges. */
function asConnection(value: unknown): ConversationConnection | undefined {
  return value && typeof value === 'object' && 'edges' in value
    ? (value as ConversationConnection)
    : undefined;
}

/** Parse the arguments out of a stored field name like `emailsByOrganization:{"organizationId":"1"}`. */
function storedArgs(storeFieldName: string): {
  organizationId?: string;
  contactEmails?: unknown;
  participantEmails?: unknown;
} {
  const start = storeFieldName.indexOf(':');
  if (start < 0) return {};
  try {
    return JSON.parse(storeFieldName.slice(start + 1)) as {
      organizationId?: string;
      contactEmails?: unknown;
      participantEmails?: unknown;
    };
  } catch {
    return {};
  }
}

/**
 * Put an email at the head of a record's cached Conversation list, without a
 * request.
 *
 * The entity must already be in the cache — a mutation that returned the whole
 * row wrote it there. A list narrowed to specific contacts or team members is dropped from the
 * cache, so it refetches, rather than edited: whether the new message belongs in
 * it depends on its recipients, which the cache should not re-derive.
 */
export function addEmailToConversation(
  cache: InMemoryCache,
  organizationId: string,
  emailId: string,
): void {
  const node = cache.identify({ __typename: 'EmailCommunication', id: emailId });
  if (!node) return;

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [FIELD](value, { storeFieldName, DELETE, toReference }) {
        const existing = asConnection(value);
        const args = storedArgs(storeFieldName);
        if (args.organizationId !== organizationId || !existing) return value;
        if (args.contactEmails !== undefined || args.participantEmails !== undefined) return DELETE;

        const ref = toReference(node);
        if (!ref) return value;
        const already = existing.edges.some((edge) => edge.node.__ref === node);
        if (already) return value;

        return {
          ...existing,
          edges: [{ __typename: 'EmailCommunicationEdge', node: ref }, ...existing.edges],
        };
      },
    },
  });
}

/**
 * Take an email out of a record's cached Conversation list, and out of the
 * cache. Matches on the row's id or its `messageId`, because a draft is
 * addressed by the latter. `keepId` spares the row a send just converted in
 * place.
 */
export function removeEmailFromConversation(
  cache: InMemoryCache,
  organizationId: string,
  emailKey: string,
  keepId?: string,
): void {
  const evicted = new Set<string>();

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      [FIELD](value, { storeFieldName, readField }) {
        const existing = asConnection(value);
        const args = storedArgs(storeFieldName);
        if (args.organizationId !== organizationId || !existing) return value;

        const edges = existing.edges.filter((edge) => {
          const id = readField<string>('id', edge.node);
          const messageId = readField<string>('messageId', edge.node);
          const matches = id === emailKey || messageId === emailKey;
          if (!matches || id === keepId) return true;
          if (edge.node.__ref) evicted.add(edge.node.__ref);
          return false;
        });
        return edges.length === existing.edges.length ? value : { ...existing, edges };
      },
    },
  });

  evicted.forEach((id) => cache.evict({ id }));
  if (evicted.size > 0) cache.gc();
}
